import React, { useState, useRef, useEffect, useMemo } from "react";
import {
    CustomCheckboxSmall,
    CustomDeleteButton,
    CustomDoubleButton,
    GraySpan,
    GreenSpan,
    IconsWrap,
    RowDiv,
    SettingsInfo,
    SourceTypeDiv,
    TickerItemDiv,
    ToolLabel,
} from "./mDPSettingsBlockStyles";
import { BasedSelect } from "components/Selects/BasedSelect";
import { UnderlinedInput } from "components/Inputs/inputsStyles";
import {
    SettingsName,
    SPSettingsValue,
    TickerItemDivForMDP,
} from "../CommonSettingsBlock/CommonSettingsBlockStyles";
import { Select, Tooltip } from "antd";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
    ConfigFileDetails,
    setConfigValidationError,
} from "store/reducers/SettingsSlice";
import TPH from "utils/trade-params-handler";
import notifyError from "utils/Notifications/Error";

type Props = {
    item: {
        SheduleID: string;
        idSource: string;
        sourceType: string;
        all_values: string[];
        SourceTypeSheduleID: string;
        isActive: boolean;
        rowId: string;
        originalIndex: number;
        isCopy: boolean;
    };
    params: string[];
    activeTickerName: string;
    MDPParamTypes: Record<string, string>;
    MDPTypes: string[];
    onToggleActive: (checked: boolean) => void;
    onDuplicate: () => void;
    onDelete: (e: any) => void;
    onFieldChange: (idx: number, value: string) => void;
    onSettingClick: () => void;
    isSavingError: boolean;
    onRowSelect?: (values: string[]) => void;
    is_horizontal: boolean;
    isRowBlocked: boolean;
    selectedRowIndex?: number | null;
    openRowsForThisConfig?: number[];
    isOpen: boolean;
    configMetadata?: ConfigFileDetails;
    configId: number;
};

const MDPSettingsItem = ({
    item,
    params,
    MDPTypes,
    onToggleActive,
    onDuplicate,
    onDelete,
    onFieldChange,
    onSettingClick,
    activeTickerName,
    MDPParamTypes,
    isSavingError,
    is_horizontal,
    onRowSelect,
    isRowBlocked,
    selectedRowIndex,
    openRowsForThisConfig,
    configMetadata,
    configId,
    isOpen,
    
}: Props) => {
    const mdpSources = useAppSelector((state) => state.settings.mdpSources);

    const stsSchedules = useAppSelector((state) => state.settings.stsSchedules);
    const scSchedules = useAppSelector(
        (state) => state.settings.scSchedules,
    );
    const descriptions = useAppSelector(
        (state) => state.settings.configParams["MDP"]?.descriptions || {},
    );
    const showDescriptions = useAppSelector(
        (state) => state.settings.showDescriptions,
    );

    const itemRef = useRef<HTMLDivElement>(null);
    const { idSource, all_values, isActive } = item;
    const isChanged = useAppSelector((state) => state.settings.isChanged);
    const [isEditedArray, setIsEditedArray] = useState<
        {
            name: string;
            value: boolean;
        }[]
    >([
        ...params.map((param, index) => {
            return { name: param, value: false };
        }),
    ]);
    const [validationErrors, setValidationErrors] = useState(
        params.map((param) => ({
            name: param,
            value: false,
        })),
    );
    const dispatch = useAppDispatch();
    const isUseIdx = params.indexOf("isUse");
    const isUseChecked = isUseIdx !== -1 ? all_values[isUseIdx] === "1" : false;

    const { Option } = Select;

    const sourceTypeSelectKey = useMemo(() => {
        return `sourceType-select-${item.rowId}-${item.sourceType}`;
    }, [item.rowId, item.sourceType]);

    useEffect(() => {
        if (isOpen && itemRef.current) {
            itemRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "nearest",
            });
        }
    }, [isOpen]);
    function removeCopySuffix(str: string) {
        if (typeof str !== "string") return;
        return str.replace(/::copy\d*$/, "");
    }
    const shouldShowField = (idx: number): boolean => {
        if (typeof params[idx] !== "string" || params[idx].length === 0) {
            return false;
        }
        // null означает, что поле не разрешено для Source.Type этой строки (см. MdpSourceTypeField)
        return all_values[idx] !== null && all_values[idx] !== undefined;
    };

    const resetConfigsCounter = useAppSelector(
        (store) => store.settings.resetConfigsCounter,
    );
    useEffect(() => {
        setValidationErrors(
            params.map((param) => ({
                name: param,
                value: false,
            })),
        );
    }, [resetConfigsCounter, params]);
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        param: string,
        idx: number,
    ) => {
        let value = e.target.value;
        const type = configMetadata?.fields?.find(
            (field) => field.name === param,
        )?.field_type;
        const isValid = TPH.validateValue(value, type);
        setIsEditedArray((prev) =>
            prev.map((el) => (el.name === param ? { ...el, value: true } : el)),
        );
        setValidationErrors((prev) =>
            prev.map((el) =>
                el.name === param ? { ...el, value: !isValid } : el,
            ),
        );

        const nextErrors = validationErrors.map((el) =>
            el.name === param ? { ...el, value: !isValid } : el,
        );

        setValidationErrors(nextErrors);

        dispatch(
            setConfigValidationError({
                configId,
                fieldKey: `${item.originalIndex}:${param}`,
                hasError: !isValid,
            }),
        );

        if (!isValid) {
            notifyError(
                `Неправильное значение для параметра "${param}". Ожидается тип: ${type}`,
            );
        }

        onFieldChange(idx, value);
    };

    const getPlaceholder = (
        param: string,
        configMetadata?: ConfigFileDetails,
    ) => {
        if (!configMetadata?.fields) {
            return "Введите значение";
        }

        const field = configMetadata.fields.find((f) => f.name === param);

        if (!field?.field_type) {
            return "Введите значение";
        }

        switch (field.field_type) {
            case "Integer":
                return "целое число";
            case "Float":
                return "число";
            case "Boolean":
                return "0 или 1";
            case "String":
                return "текст";
            default:
                return "Введите значение";
        }
    };
    const renderValueInput = (param: string, idx: number) => {
        let currentValue = all_values[idx];
        if (currentValue === null || currentValue === undefined) {
            currentValue = "";
        }
        const fieldMeta = configMetadata?.fields?.find((f) => f.name === param);
        const isFieldEditable = fieldMeta?.is_editable;
        const isDisabled =
            configMetadata?.readonly ||
            (isRowBlocked && !isOpen) ||
            isFieldEditable === false;

        if (param === "ID.source") {
            return (
                <BasedSelect
                    size="small"
                    value={currentValue || ""}
                    onChange={(value) => onFieldChange(idx, value)}
                    disabled={isDisabled}
                    style={{ width: "100%" }}
                >
                    {mdpSources?.map((source) => (
                        <Option key={source} value={source}>
                            {source}
                        </Option>
                    )) || <Option disabled>Нет источников</Option>}
                </BasedSelect>
            );
        }
        if (param === "SheduleID"|| param === "ScheduleID") {
            return (
                <BasedSelect
                    value={currentValue}
                    onChange={(value) => onFieldChange(idx, value)}
                    disabled={isDisabled}
                    style={{
                        width: "100%",
                    }}
                    dropdownStyle={{ zIndex: 1050 }}
                >
                    {(scSchedules || []).map((schedule) => (
                        <Option key={schedule.id} value={schedule.id}>
                            {schedule.id}: {schedule.start_time}-{schedule.end_time}
                        </Option>
                    ))}
                </BasedSelect>
            );
        }
        if (param === "Source.Type") {
            return (
                <BasedSelect
                    key={sourceTypeSelectKey}
                    size="small"
                    value={currentValue}
                    onChange={(value) => onFieldChange(idx, value)}
                    disabled={isDisabled}
                    style={{ width: "100%" }}
                >
                    {MDPTypes.map((t) => (
                        <Option key={t} value={t}>
                            {t}
                        </Option>
                    ))}
                </BasedSelect>
            );
        }
        if (param === "Source.type.shedule.ID") {
            return (
                <BasedSelect
                    key={currentValue}
                    size="small"
                    value={currentValue || ""}
                    onChange={(value) => onFieldChange(idx, value)}
                    disabled={isDisabled}
                    style={{ width: "100%" }}
                >
                    {stsSchedules.map((schedule) => (
                        <Option key={schedule.id} value={schedule.id}>
                            {schedule.id}: {schedule.start_time} -{" "}
                            {schedule.end_time}
                        </Option>
                    ))}
                </BasedSelect>
            );
        }
        return (
            <UnderlinedInput
                key={`input-${param}`}
                isEdited={isEditedArray.find((el) => el.name === param)?.value}
                isError={
                    validationErrors.find((el) => el.name === param)?.value
                }
                type="text"
                value={currentValue}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleChange(e, param, idx)}
                disabled={isDisabled}
                placeholder={getPlaceholder(param, configMetadata)}
                style={{
                    cursor: isDisabled ? "not-allowed" : "text",
                }}
            />
        );
    };

    return (
        <div ref={itemRef}>
            <TickerItemDiv
                style={{
                    cursor:
                        (isRowBlocked && !isOpen) || !isActive
                            ? "default"
                            : "pointer",
                    opacity:
                        (isRowBlocked && !isOpen) || !isActive || isOpen
                            ? 0.6
                            : 1,
                }}
                isSelected={isActive}
                onClick={(e) => {
                    if (isRowBlocked || !isActive) return;
                    e.stopPropagation();

                    if (onRowSelect) {
                        onRowSelect(all_values);
                    }
                }}
            >
                <SettingsInfo>
                    <RowDiv>
                        <CustomCheckboxSmall
                            checked={isUseChecked}
                            onChange={(e) => {
                                if (isRowBlocked) return;
                                e.stopPropagation();
                                onToggleActive(e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <ToolLabel
                            onClick={isActive ? onSettingClick : undefined}
                        >
                            {removeCopySuffix(idSource)}
                        </ToolLabel>
                    </RowDiv>
                    <SourceTypeDiv>
                        <GraySpan>SC - </GraySpan>{" "}
                        <GreenSpan>{item.SheduleID}</GreenSpan>
                        <GraySpan> | </GraySpan>
                        <GraySpan>Type - </GraySpan>{" "}
                        <GreenSpan>{item.sourceType}</GreenSpan>
                        <GraySpan> | </GraySpan>
                        <GraySpan>S.T.SheduleID -</GraySpan>{" "}
                        <GreenSpan>{item.SourceTypeSheduleID}</GreenSpan>
                    </SourceTypeDiv>
                </SettingsInfo>

                <IconsWrap>
                    <CustomDeleteButton
                        style={{
                            opacity: isChanged ? 0.4 : 1,
                            filter: isChanged ? "grayscale(100%)" : "none",
                            cursor: isChanged ? "not-allowed" : "pointer",
                            pointerEvents: isChanged ? "none" : "auto",
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (isChanged) return;
                            onDelete(e);
                        }}
                    />
                    <CustomDoubleButton
                        onClick={(e) => {
                            e.stopPropagation();
                            onDuplicate();
                        }}
                    />
                </IconsWrap>
            </TickerItemDiv>
            {isOpen &&
                !is_horizontal &&
                params.map((param, idx) => {
                    if (!shouldShowField(idx)) return null;
                    const description = descriptions[param] || "";
                    return (
                        <TickerItemDivForMDP
                            key={`${activeTickerName}-${param}`}
                        >
                            {showDescriptions && description ? (
                                <Tooltip
                                    title={description}
                                    placement="bottom"
                                    mouseEnterDelay={0.3}
                                >
                                    <SettingsName style={{ cursor: "help" }}>
                                        {removeCopySuffix(param)}
                                    </SettingsName>
                                </Tooltip>
                            ) : (
                                <SettingsName style={{ cursor: "default" }}>
                                    {removeCopySuffix(param)}
                                </SettingsName>
                            )}
                            <SPSettingsValue>
                                {renderValueInput(param, idx)}
                            </SPSettingsValue>
                        </TickerItemDivForMDP>
                    );
                })}
        </div>
    );
};

export default React.memo(MDPSettingsItem);
